import { getSuiteBySlug } from '@/features/suites/data/suites';
import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapQueryResult, mapSuiteExecutionDto } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { buildEqFilter, mergeFilters, type GridFilters } from '@/ui/composites/grid/gridFilters';
import type {
  QueryFilter,
  QueryResult,
  QuerySort,
  SuiteExecutionDto,
  SuiteExecutionStatusSummaryDto,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (tokenOverride?: string | null): string => {
  const token = tokenOverride ?? getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

const suiteExecutionSortFieldMap: Readonly<Record<string, string>> = {
  runNumber: 'timestamp',
};

const mapSuiteExecutionSorts = (sorts: QuerySort[]): QuerySort[] => {
  const mappedSorts: QuerySort[] = [];
  for (const sort of sorts) {
    const field = typeof sort.field === 'string' ? sort.field : String(sort.field);
    const direction = sort.direction === 'desc' ? 'desc' : 'asc';
    const mappedField = Object.prototype.hasOwnProperty.call(suiteExecutionSortFieldMap, field)
      ? suiteExecutionSortFieldMap[field]
      : field;
    mappedSorts.push({ field: mappedField, direction });
  }
  return mappedSorts;
};

export const querySuiteExecutions = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    include?: SuiteExecutionInclude;
    includeSummary?: boolean;
  },
  tokenOverride?: string | null,
): Promise<QueryResult<SuiteExecutionDto>> => {
  const token = ensureToken(tokenOverride);
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters =
    !hasProjectFilter && projectId ? buildEqFilter('projectId', projectId) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const sorts = input?.sorts
    ? mapSuiteExecutionSorts(input.sorts)
    : [{ field: 'timestamp', direction: 'desc' }];
  try {
    const result = await apiClient.post<QueryResult<SuiteExecutionDto>>(
      'suite-executions/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts,
        filters: combinedFilters,
        include: input?.include,
        includeSummary: input?.includeSummary,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapSuiteExecutionDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const querySuiteExecutionStatusSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    include?: SuiteExecutionInclude;
  },
  tokenOverride?: string | null,
): Promise<SuiteExecutionStatusSummaryDto> => {
  const token = ensureToken(tokenOverride);
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters =
    !hasProjectFilter && projectId ? buildEqFilter('projectId', projectId) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const sorts = input?.sorts ? mapSuiteExecutionSorts(input.sorts) : [];
  try {
    return await apiClient.post<SuiteExecutionStatusSummaryDto>(
      'suite-executions/summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 1,
        sorts,
        filters: combinedFilters,
        include: input?.include,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getSuiteExecutionById = async (
  id: string,
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<SuiteExecutionDto> => {
  const token = ensureToken(tokenOverride);
  try {
    const result = await apiClient.get<SuiteExecutionDto>(`suite-executions/${id}`, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return mapSuiteExecutionDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const getSuiteExecutionByRunNumber = async (
  suiteSlug: string | null,
  runNumber: number | null,
  projectId?: string | null,
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<SuiteExecutionDto | null> => {
  if (!suiteSlug || runNumber === null || !Number.isFinite(runNumber) || runNumber < 1) {
    return null;
  }
  let suite = await getSuiteBySlug(suiteSlug, projectId, organisationId, tokenOverride);
  if (!suite && projectId) {
    suite = await getSuiteBySlug(suiteSlug, null, organisationId, tokenOverride);
  }
  if (!suite) {
    return null;
  }
  const resolvedProjectId = suite.projectId;
  const filters = buildEqFilter('suiteId', suite.id);
  const pageSize = 100;
  const runSorts = [
    { field: 'timestamp', direction: 'asc' },
    { field: 'id', direction: 'asc' },
  ] satisfies QuerySort[];
  const firstPage = await querySuiteExecutions(
    resolvedProjectId,
    organisationId,
    {
      page: 1,
      pageSize,
      sorts: runSorts,
      filters,
      include: { resultsFile: true, resultsFileRunGroup: true, suite: true, project: true },
    },
    tokenOverride,
  );
  const firstMatch = firstPage.items.find((item) => item.runNumber === runNumber);
  if (firstMatch) return firstMatch;
  const maxPage = Math.max(1, Math.ceil(firstPage.total / pageSize));
  if (maxPage === 1) return null;
  const targetPage = Math.min(maxPage, Math.max(2, Math.ceil(runNumber / pageSize)));
  if (targetPage !== 1) {
    const targetResult = await querySuiteExecutions(
      resolvedProjectId,
      organisationId,
      {
        page: targetPage,
        pageSize,
        sorts: runSorts,
        filters,
        include: { resultsFile: true, resultsFileRunGroup: true, suite: true, project: true },
      },
      tokenOverride,
    );
    const targetMatch = targetResult.items.find((item) => item.runNumber === runNumber);
    if (targetMatch) return targetMatch;
  }
  for (let page = 2; page <= maxPage; page += 1) {
    if (page === targetPage) continue;
    const result = await querySuiteExecutions(
      resolvedProjectId,
      organisationId,
      {
        page,
        pageSize,
        sorts: runSorts,
        filters,
        include: { resultsFile: true, resultsFileRunGroup: true, suite: true, project: true },
      },
      tokenOverride,
    );
    const match = result.items.find((item) => item.runNumber === runNumber);
    if (match) return match;
    if (result.items.length < pageSize) break;
  }
  return null;
};

export const createSuiteExecution = async (
  input: {
    runId: string;
    suiteId: string;
    status?: SuiteExecutionDto['status'];
    timestamp?: Date | string | null;
    name?: string | null;
    classname?: string | null;
    file?: string | null;
    durationMs?: number | null;
    tests?: number | null;
    failures?: number | null;
    errors?: number | null;
    skipped?: number | null;
    systemOut?: string | null;
    systemErr?: string | null;
  },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<SuiteExecutionDto> => {
  const token = ensureToken(tokenOverride);
  const body = {
    runId: input.runId.trim(),
    suiteId: input.suiteId.trim(),
    status: input.status,
    timestamp: input.timestamp ?? undefined,
    name: input.name ?? undefined,
    classname: input.classname ?? undefined,
    file: input.file ?? undefined,
    durationMs: input.durationMs ?? undefined,
    tests: input.tests ?? undefined,
    failures: input.failures ?? undefined,
    errors: input.errors ?? undefined,
    skipped: input.skipped ?? undefined,
    systemOut: input.systemOut ?? undefined,
    systemErr: input.systemErr ?? undefined,
  };
  try {
    const result = await apiClient.post<SuiteExecutionDto>('suite-executions', body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapSuiteExecutionDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateSuiteExecution = async (
  input: {
    id: string;
    status?: SuiteExecutionDto['status'];
    name?: string | null;
    classname?: string | null;
    file?: string | null;
    durationMs?: number | null;
    tests?: number | null;
    failures?: number | null;
    errors?: number | null;
    skipped?: number | null;
    systemOut?: string | null;
    systemErr?: string | null;
  },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<SuiteExecutionDto> => {
  const token = ensureToken(tokenOverride);
  const body = {
    status: input.status,
    name: input.name ?? undefined,
    classname: input.classname ?? undefined,
    file: input.file ?? undefined,
    durationMs: input.durationMs ?? undefined,
    tests: input.tests ?? undefined,
    failures: input.failures ?? undefined,
    errors: input.errors ?? undefined,
    skipped: input.skipped ?? undefined,
    systemOut: input.systemOut ?? undefined,
    systemErr: input.systemErr ?? undefined,
  };
  try {
    const result = await apiClient.patch<SuiteExecutionDto>(`suite-executions/${input.id}`, body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapSuiteExecutionDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export type SuiteExecutionInclude = {
  suite?: boolean;
  project?: boolean;
  run?: boolean;
  resultsFile?: boolean;
  resultsFileCreatedBy?: boolean;
  resultsFileRunGroup?: boolean;
};

export const deleteSuiteExecution = async (
  input: { id: string },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken(tokenOverride);
  try {
    await apiClient.post<undefined>(`suite-executions/${input.id}/delete`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const restoreSuiteExecution = async (
  input: { id: string },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken(tokenOverride);
  try {
    await apiClient.post<undefined>(`suite-executions/${input.id}/restore`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};
